'use client';

import { useEffect, useState } from 'react';

import { useAuthStore } from '@/store/auth-store';

/** Max wait before treating auth as hydrated so UI never stays disabled if persist callbacks stall (e.g. Strict Mode races). */
const HYDRATION_SAFETY_MS = 2_500;
const HYDRATION_POLL_MS = 100;

/** Ensures persisted auth state has hydrated before routing decisions run client-side */
export function useAuthHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const markReady = () => {
      queueMicrotask(() => {
        if (!cancelled) {
          setHydrated(true);
        }
      });
    };

    /** Prefer `hasHydrated()` directly — `onFinishHydration` may have already fired before we subscribe. */
    const tryHydrated = (): boolean => {
      if (!useAuthStore.persist.hasHydrated()) {
        return false;
      }
      markReady();
      return true;
    };

    if (tryHydrated()) {
      return () => {
        cancelled = true;
      };
    }

    const unsub = useAuthStore.persist.onFinishHydration(() => {
      tryHydrated();
    });

    const poll = window.setInterval(() => {
      if (tryHydrated()) {
        window.clearInterval(poll);
        window.clearTimeout(safety);
      }
    }, HYDRATION_POLL_MS);

    const safety = window.setTimeout(() => {
      window.clearInterval(poll);
      markReady();
    }, HYDRATION_SAFETY_MS);

    return () => {
      cancelled = true;
      unsub();
      window.clearInterval(poll);
      window.clearTimeout(safety);
    };
  }, []);

  return hydrated;
}
